import React, { useState, useEffect, useRef } from 'react';
import { TrendingUp, LogIn, Mail, Lock, BarChart3 } from 'lucide-react';
import Dashboard from './components/Dashboard';
import CampaignsView from './components/CampaignsView';
import CreativesView from './components/CreativesView';
import AnalyticsView from './components/AnalyticsView';
import BillingView from './components/BillingView';
import SettingsView from './components/SettingsView';
import NotificationsView from './components/NotificationsView';
import Navigation from './components/Navigation';
import OnboardingTour, { OnboardingStep } from './components/OnboardingTour';
import { createClient } from '@supabase/supabase-js';
import { adsApiService } from './services/adsApiService';

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL as string,
  import.meta.env.VITE_SUPABASE_ANON_KEY as string
);

function App() {
  const [activeView, setActiveView] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const ensuringAccountRef = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const returnTo = params.get('return_to');
    const allowedViews = new Set(['dashboard', 'campaigns', 'creatives', 'analytics', 'billing', 'settings', 'notifications']);
    if (returnTo && allowedViews.has(returnTo)) {
      setActiveView(returnTo);
      params.delete('return_to');
      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`.replace(/\?$/, ''));
    }

    initializeApp();
    
    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (session?.user) {
          setUser(session.user);
          void ensureUserHasAdAccount();
          setLoading(false);
        } else {
          setUser(null);
          setLoading(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const ensureUserHasAdAccount = async () => {
    if (ensuringAccountRef.current) return;
    ensuringAccountRef.current = true;
    try {
      await adsApiService.setupUserAccount({
        account_name: 'Mi Cuenta Publicitaria',
        account_type: 'business',
      });
    } catch (error) {
      console.error('Error ensuring ad account exists:', error);
    } finally {
      ensuringAccountRef.current = false;
    }
  };

  const initializeApp = async () => {
    try {
      setLoading(true);
      
      // Check for existing session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        setUser(session.user);
        void ensureUserHasAdAccount();
      }

    } catch (error) {
      console.error('Error initializing app:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Prevent double submission
    if (submitting || loginLoading) {
      return;
    }
    
    setSubmitting(true);
    setLoginLoading(true);
    setLoginError('');

    try {
      const { data: authResult, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        setLoginError(error.message);
      } else if (authResult?.user) {
        setUser(authResult.user);
      }
    } catch (error) {
      setLoginError('Error al iniciar sesión');
    } finally {
      setLoginLoading(false);
      setSubmitting(false);
    }
  };

  const handleSocialAuth = async (provider: 'google' | 'apple') => {
    if (submitting || loginLoading) {
      return;
    }
    
    setSubmitting(true);
    setLoginLoading(true);
    setLoginError('');

    try {
      const { data: _authResult, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: window.location.origin,
        },
      });

      if (error) {
        setLoginError('Error al autenticar con ' + provider);
      }
      // _authResult is not directly used here, but it contains session and user info
      // which might be implicitly handled by onAuthStateChange listener.
      // Renamed to _authResult to suppress 'data' declared but not read warning.
    } catch (error) {
      setLoginError('Error al autenticar con ' + provider);
    } finally {
      setLoginLoading(false);
      setSubmitting(false);
    }
  };

  const renderActiveView = () => {
    switch (activeView) {
      case 'dashboard':
        return <Dashboard onViewChange={setActiveView} />;
      case 'campaigns':
        return <CampaignsView />;
      case 'creatives':
        return <CreativesView />;
      case 'analytics':
        return <AnalyticsView onViewChange={setActiveView} />;
      case 'billing':
        return <BillingView />;
      case 'settings':
        return <SettingsView user={user} />;
      case 'notifications':
        return <NotificationsView />;
      default:
        return <Dashboard onViewChange={setActiveView} />;
    }
  };

  const onboardingSteps: OnboardingStep[] = [
    {
      id: 'welcome',
      title: 'Bienvenido a SoArtistic Ads',
      body: 'Te explico en 1 minuto para qué sirve cada sección. Puedes saltarlo cuando quieras.',
    },
    {
      id: 'nav-dashboard',
      title: 'Inicio',
      body: 'Aquí ves el resumen de rendimiento y accesos rápidos para crear cosas.',
      target: '[data-tour="nav-dashboard"]',
      view: 'dashboard',
    },
    {
      id: 'nav-campaigns',
      title: 'Campañas',
      body: 'Las campañas son lo que hace que tu arte se muestre a más gente. Crea, pausa o edita campañas desde aquí.',
      target: '[data-tour="nav-campaigns"]',
    },
    {
      id: 'dash-new-campaign',
      title: 'Nueva campaña (atajo)',
      body: 'Acceso rápido para crear una campaña sin perder tiempo.',
      target: '[data-tour="dash-new-campaign"]',
      view: 'dashboard',
    },
    {
      id: 'campaigns-new',
      title: 'Crear campaña',
      body: 'Este botón abre el asistente para configurar objetivo, presupuesto y creativos.',
      target: '[data-tour="campaigns-new"]',
      view: 'campaigns',
    },
    {
      id: 'nav-creatives',
      title: 'Creativos',
      body: 'Los creativos son las piezas (imagen/video/texto) que se usan dentro de las campañas.',
      target: '[data-tour="nav-creatives"]',
    },
    {
      id: 'creatives-new',
      title: 'Nuevo creativo',
      body: 'Crea aquí el anuncio visual (imagen/video) y reutilízalo en distintas campañas.',
      target: '[data-tour="creatives-new"]',
      view: 'creatives',
    },
    {
      id: 'dash-analytics',
      title: 'Informes',
      body: 'En Analíticas verás impresiones, clics y gasto por campaña para optimizar tu inversión.',
      target: '[data-tour="dash-analytics"]',
      view: 'dashboard',
    },
    {
      id: 'finish',
      title: 'Listo',
      body: 'Ya puedes empezar: crea un creativo, luego una campaña, y revisa los informes para mejorar resultados.',
    },
  ];

  // Show login form if no user is authenticated
  if (!loading && !user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-700 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl flex items-center justify-center mx-auto mb-4">
              <BarChart3 className="text-white" size={32} />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">SoArtistic Ads Manager</h1>
            <p className="text-gray-600">Inicia sesión para acceder al panel de publicidad</p>
          </div>

          {/* Social Auth Buttons */}
          <div className="space-y-3 mb-6">
            <button
              type="button"
              onClick={() => handleSocialAuth('google')}
              disabled={loginLoading || submitting}
              className="w-full flex items-center justify-center gap-3 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-blue-400 transition-all duration-200 disabled:opacity-50"
            >
              <div className="w-5 h-5 bg-white rounded-sm flex items-center justify-center shadow-sm">
                <svg viewBox="0 0 24 24" className="w-4 h-4">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              <span className="font-medium text-gray-700">Continuar con Google</span>
            </button>
            
            <button
              type="button"
              onClick={() => handleSocialAuth('apple')}
              disabled={loginLoading || submitting}
              className="w-full flex items-center justify-center gap-3 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-800 transition-all duration-200 disabled:opacity-50"
            >
              <div className="w-5 h-5 bg-black rounded-sm flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-4 h-4 text-white">
                  <path fill="currentColor" d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
              </div>
              <span className="font-medium text-gray-700">Continuar con Apple</span>
            </button>
            
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500 font-medium">o continúa con email</span>
              </div>
            </div>
          </div>

          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="tu@email.com"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contraseña
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="••••••••"
                  required
                />
              </div>
            </div>

            {loginError && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm">
                {loginError}
              </div>
            )}

            <button
              type="submit"
              disabled={loginLoading || submitting}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-6 rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
            >
              {loginLoading || submitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Iniciando sesión...
                </>
              ) : (
                <>
                  <LogIn size={18} />
                  Iniciar Sesión
                </>
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              ¿No tienes cuenta? 
              <a 
                href="http://localhost:5173" 
                className="text-purple-600 hover:text-purple-700 font-medium ml-1"
              >
                Regístrate en SoArtistic
              </a>
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-blue-600 rounded-xl flex items-center justify-center mx-auto mb-4 animate-pulse-blue">
            <TrendingUp className="text-white" size={32} />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">SoArtistic Ads Manager</h2>
          <p className="text-gray-600">Cargando panel de control...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Navigation 
        activeView={activeView} 
        onViewChange={setActiveView}
        user={user}
      />
      
      <div className="flex-1 lg:ml-64 pb-16 lg:pb-0">
        <main className="min-h-screen">
          {renderActiveView()}
        </main>
      </div>

      <OnboardingTour
        userId={user?.id || null}
        activeView={activeView}
        onViewChange={setActiveView}
        steps={onboardingSteps}
      />
    </div>
  );
}

export default App;
